import { useState, useEffect } from 'preact/hooks';
import { Link } from 'preact-router/match';
import { superAdminFetch, superAdminPut, superAdminPost } from '../../lib/super-admin-api';

const API_URL = import.meta.env.VITE_API_URL || 'https://api.unplug-app.com';

export function SuperAdminSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [xeroSettings, setXeroSettings] = useState({
    connected: false,
    tenant_id: '',
    tenant_name: '',
    sales_account_code: '400',
    vat_account_code: '820',
    sales_tax_type: 'OUTPUT',
    reverse_charge_tax_type: 'ZERORATEDOUTPUT',
    exempt_tax_type: 'EXEMPTOUTPUT',
    token_expires_at: null
  });

  const [taxRates, setTaxRates] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loadingTaxRates, setLoadingTaxRates] = useState(false);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [taxRatesError, setTaxRatesError] = useState(null);
  const [accountsError, setAccountsError] = useState(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  async function fetchSettings() {
    try {
      const res = await superAdminFetch(`${API_URL}/api/super-admin/xero/settings`);
      if (res.ok) {
        const data = await res.json();
        setXeroSettings(data);

        // If connected, fetch tax rates and accounts
        if (data.connected) {
          fetchTaxRates();
          fetchAccounts();
        }
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function fetchTaxRates() {
    setLoadingTaxRates(true);
    setTaxRatesError(null);
    try {
      const res = await superAdminFetch(`${API_URL}/api/super-admin/xero/tax-rates`);
      const data = await res.json();

      if (res.ok) {
        setTaxRates(data.tax_rates || []);
        if ((data.tax_rates || []).length === 0) {
          setTaxRatesError('No tax rates found in Xero that can apply to revenue');
        }
      } else {
        setTaxRatesError(data.error || 'Failed to fetch tax rates');
      }
    } catch (err) {
      console.error('Failed to fetch tax rates:', err);
      setTaxRatesError(err.message || 'Network error fetching tax rates');
    } finally {
      setLoadingTaxRates(false);
    }
  }

  async function fetchAccounts() {
    setLoadingAccounts(true);
    setAccountsError(null);
    try {
      const res = await superAdminFetch(`${API_URL}/api/super-admin/xero/accounts`);
      const data = await res.json();

      if (res.ok) {
        setAccounts(data.accounts || []);
        if ((data.accounts || []).length === 0) {
          setAccountsError('No revenue accounts found in Xero');
        }
      } else {
        setAccountsError(data.error || 'Failed to fetch accounts');
      }
    } catch (err) {
      console.error('Failed to fetch accounts:', err);
      setAccountsError(err.message || 'Network error fetching accounts');
    } finally {
      setLoadingAccounts(false);
    }
  }

  async function handleSaveXeroSettings(e) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const res = await superAdminPut(`${API_URL}/api/super-admin/xero/settings`, {
        sales_account_code: xeroSettings.sales_account_code,
        vat_account_code: xeroSettings.vat_account_code,
        sales_tax_type: xeroSettings.sales_tax_type,
        reverse_charge_tax_type: xeroSettings.reverse_charge_tax_type,
        exempt_tax_type: xeroSettings.exempt_tax_type
      });

      if (!res.ok) {
        throw new Error('Failed to save settings');
      }

      setSuccess('Xero settings saved successfully');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleConnectXero() {
    setError(null);
    try {
      const response = await superAdminPost(`${API_URL}/api/super-admin/xero/connect`, {});
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Unable to connect Xero');
      window.location.href = data.url;
    } catch (err) {
      setError(err.message);
    }
  }

  if (loading) {
    return (
      <div class="super-admin-page">
        <div class="loading-state">
          <div class="spinner" />
          <p>Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div class="super-admin-page">
      <div class="page-header">
        <div>
          <Link href="/super-admin" class="back-link">&larr; Back to Dashboard</Link>
          <h1>Settings</h1>
        </div>
      </div>

      {error && (
        <div class="alert alert-error mb-4">
          {error}
        </div>
      )}

      {success && (
        <div class="alert alert-success mb-4">
          {success}
        </div>
      )}

      {/* Xero Integration */}
      <div class="card">
        <div class="card-header">
          <h2>Xero Integration</h2>
          <span class={`badge badge-${xeroSettings.connected ? 'success' : 'warning'}`}>
            {xeroSettings.connected ? 'Connected' : 'Not Connected'}
          </span>
        </div>
        <div class="card-body">
          {!xeroSettings.connected ? (
            <div class="text-center py-4">
              <p class="text-muted mb-4">Connect to Xero to enable automatic invoice creation.</p>
              <button class="btn btn-primary" onClick={handleConnectXero}>
                Connect to Xero
              </button>
            </div>
          ) : (
            <>
              <div class="info-box mb-4">
                <dl class="info-list">
                  <dt>Tenant</dt>
                  <dd>{xeroSettings.tenant_name || xeroSettings.tenant_id || '-'}</dd>

                  <dt>Token Expires</dt>
                  <dd>
                    {xeroSettings.token_expires_at
                      ? new Date(xeroSettings.token_expires_at).toLocaleString()
                      : '-'}
                  </dd>
                </dl>
              </div>

              <form onSubmit={handleSaveXeroSettings}>
                <h3 class="mb-3">Invoice Settings</h3>

                <div class="form-group">
                  <label for="sales_account_code">Sales Account Code</label>
                  {loadingAccounts ? (
                    <p class="text-muted">Loading accounts...</p>
                  ) : accounts.length > 0 ? (
                    <select
                      id="sales_account_code"
                      value={xeroSettings.sales_account_code}
                      onChange={e => setXeroSettings(prev => ({ ...prev, sales_account_code: e.target.value }))}
                      class="form-select"
                    >
                      <option value="">Select an account...</option>
                      {accounts.map(acc => (
                        <option key={acc.code} value={acc.code}>
                          {acc.code} - {acc.name}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <>
                      <input
                        type="text"
                        id="sales_account_code"
                        value={xeroSettings.sales_account_code}
                        onChange={e => setXeroSettings(prev => ({ ...prev, sales_account_code: e.target.value }))}
                        placeholder="e.g., 400"
                        class="form-input"
                      />
                      {accountsError && (
                        <small class="text-error">{accountsError}</small>
                      )}
                    </>
                  )}
                  <small class="text-muted">
                    The revenue account for invoice line items
                  </small>
                </div>

                <h4 class="mt-4 mb-2">Tax Types by Scenario</h4>
                <p class="text-muted mb-3" style="font-size: 0.9rem;">
                  Select the appropriate tax rate for each VAT scenario.
                </p>

                <div class="form-group">
                  <label for="sales_tax_type">Malta / EU B2C (18% VAT)</label>
                  {loadingTaxRates ? (
                    <p class="text-muted">Loading tax rates...</p>
                  ) : taxRates.length > 0 ? (
                    <select
                      id="sales_tax_type"
                      value={xeroSettings.sales_tax_type}
                      onChange={e => setXeroSettings(prev => ({ ...prev, sales_tax_type: e.target.value }))}
                      class="form-select"
                    >
                      <option value="">Select a tax rate...</option>
                      {taxRates.map(tr => (
                        <option key={tr.tax_type} value={tr.tax_type}>
                          {tr.name} ({tr.rate}%)
                        </option>
                      ))}
                    </select>
                  ) : (
                    <>
                      <input
                        type="text"
                        id="sales_tax_type"
                        value={xeroSettings.sales_tax_type}
                        onChange={e => setXeroSettings(prev => ({ ...prev, sales_tax_type: e.target.value }))}
                        placeholder="Tax type code"
                        class="form-input"
                      />
                      {taxRatesError && (
                        <small class="text-error">{taxRatesError}</small>
                      )}
                    </>
                  )}
                  <small class="text-muted">
                    Used for: Malta customers, EU customers without valid VAT number
                  </small>
                </div>

                <div class="form-group">
                  <label for="reverse_charge_tax_type">EU B2B Reverse Charge (0%)</label>
                  {loadingTaxRates ? (
                    <p class="text-muted">Loading tax rates...</p>
                  ) : taxRates.length > 0 ? (
                    <select
                      id="reverse_charge_tax_type"
                      value={xeroSettings.reverse_charge_tax_type}
                      onChange={e => setXeroSettings(prev => ({ ...prev, reverse_charge_tax_type: e.target.value }))}
                      class="form-select"
                    >
                      <option value="">Select a tax rate...</option>
                      {taxRates.map(tr => (
                        <option key={tr.tax_type} value={tr.tax_type}>
                          {tr.name} ({tr.rate}%)
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="text"
                      id="reverse_charge_tax_type"
                      value={xeroSettings.reverse_charge_tax_type}
                      onChange={e => setXeroSettings(prev => ({ ...prev, reverse_charge_tax_type: e.target.value }))}
                      placeholder="Tax type code"
                      class="form-input"
                    />
                  )}
                  <small class="text-muted">
                    Used for: EU business customers with valid VAT number
                  </small>
                </div>

                <div class="form-group">
                  <label for="exempt_tax_type">Non-EU / Outside Scope (0%)</label>
                  {loadingTaxRates ? (
                    <p class="text-muted">Loading tax rates...</p>
                  ) : taxRates.length > 0 ? (
                    <select
                      id="exempt_tax_type"
                      value={xeroSettings.exempt_tax_type}
                      onChange={e => setXeroSettings(prev => ({ ...prev, exempt_tax_type: e.target.value }))}
                      class="form-select"
                    >
                      <option value="">Select a tax rate...</option>
                      {taxRates.map(tr => (
                        <option key={tr.tax_type} value={tr.tax_type}>
                          {tr.name} ({tr.rate}%)
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="text"
                      id="exempt_tax_type"
                      value={xeroSettings.exempt_tax_type}
                      onChange={e => setXeroSettings(prev => ({ ...prev, exempt_tax_type: e.target.value }))}
                      placeholder="Tax type code"
                      class="form-input"
                    />
                  )}
                  <small class="text-muted">
                    Used for: Non-EU customers (outside scope of Malta VAT)
                  </small>
                </div>

                <div class="form-actions mt-4">
                  <button type="submit" class="btn btn-primary" disabled={saving}>
                    {saving ? 'Saving...' : 'Save Xero Settings'}
                  </button>
                  <button
                    type="button"
                    class="btn btn-secondary ml-2"
                    onClick={() => { fetchTaxRates(); fetchAccounts(); }}
                    disabled={loadingTaxRates || loadingAccounts}
                  >
                    Refresh from Xero
                  </button>
                  <button
                    type="button"
                    class="btn btn-outline ml-2"
                    onClick={handleConnectXero}
                  >
                    Re-authorize Xero
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>

      <style jsx>{`
        .info-box {
          background: var(--bg-secondary);
          padding: 1rem;
          border-radius: 8px;
        }
        .form-group {
          margin-bottom: 1.5rem;
        }
        .form-group label {
          display: block;
          font-weight: 500;
          margin-bottom: 0.5rem;
        }
        .form-input, .form-select {
          width: 100%;
          max-width: 400px;
          padding: 0.5rem 0.75rem;
          border: 1px solid var(--border-color);
          border-radius: 6px;
          font-size: 0.95rem;
          background: white;
        }
        .form-input:focus, .form-select:focus {
          outline: none;
          border-color: var(--primary);
          box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
        }
        small.text-muted {
          display: block;
          margin-top: 0.25rem;
          font-size: 0.85rem;
        }
        small.text-error {
          display: block;
          margin-top: 0.25rem;
          font-size: 0.85rem;
          color: #dc2626;
        }
        .form-actions {
          display: flex;
          gap: 0.5rem;
          flex-wrap: wrap;
        }
        .alert {
          padding: 1rem;
          border-radius: 8px;
        }
        .alert-error {
          background: #fef2f2;
          color: #dc2626;
          border: 1px solid #fecaca;
        }
        .alert-success {
          background: #f0fdf4;
          color: #16a34a;
          border: 1px solid #bbf7d0;
        }
        h3 {
          font-size: 1.1rem;
          font-weight: 600;
        }
        h4 {
          font-size: 1rem;
          font-weight: 600;
        }
        .btn-outline {
          background: transparent;
          border: 1px solid var(--border-color);
          color: var(--text-primary);
        }
        .btn-outline:hover {
          background: var(--bg-secondary);
        }
        .back-link {
          display: inline-block;
          margin-bottom: 0.5rem;
          color: var(--primary);
          text-decoration: none;
          font-size: 0.9rem;
        }
        .back-link:hover {
          text-decoration: underline;
        }
      `}</style>
    </div>
  );
}
